import type { StaffAnalyticsVisit } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { DataTable } from "../../ui/data-table";
import { durationText } from "../format";
import { ENTRY_LABELS, PLATFORM_LABELS } from "./metric-catalog";

// "Visits in this range": when, device, length, screens, where the visit came from and what the customer's records show while it lasted. "Open
// visit" opens the Events tab with the visit in the address: the Events tab (CHT-2030) reads `visit` and shows the visit step by step. The sentence
// "what happened" is made here from counts of records made during the visit.

const column = createColumnHelper<StaffAnalyticsVisit>();

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** What the records show for the visit, in words: "1 request, 1 payment". It says when nothing was recorded; it never says why. */
function happenedText(records: StaffAnalyticsVisit["records"]): string {
  const parts = [
    records.requests > 0 ? plural(records.requests, "request", "requests") : null,
    records.payments > 0 ? plural(records.payments, "payment", "payments") : null,
    records.messages > 0 ? plural(records.messages, "message", "messages") : null,
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? "Nothing recorded" : parts.join(", ");
}

const whenText = (iso: string, tz: string) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));

export function VisitsTable({
  visits,
  tz,
  eventsHref,
  hasMore,
  loadingMore,
  onMore,
}: Readonly<{
  visits: readonly StaffAnalyticsVisit[];
  tz: string;
  /** The Events tab address of a visit. */
  eventsHref: (visitId: string) => string;
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}>) {
  const columns = [
    column.accessor("startedAt", {
      header: "When",
      meta: { rowHeader: true, className: "font-semibold" },
      cell: (info) => whenText(info.getValue(), tz),
    }),
    column.accessor("platform", {
      header: "Device",
      cell: (info) => PLATFORM_LABELS[info.getValue() ?? "unknown"],
    }),
    column.accessor("durationSeconds", {
      header: "Length",
      cell: (info) => durationText(info.getValue()),
    }),
    column.accessor("screenCount", { header: "Screens" }),
    column.accessor("entry", {
      header: "Came from",
      cell: (info) => ENTRY_LABELS[info.getValue() ?? "unknown"],
    }),
    column.display({
      id: "happened",
      header: "What happened",
      cell: (info) => happenedText(info.row.original.records),
    }),
    column.display({
      id: "open",
      header: "Open visit",
      meta: { label: "Open visit" },
      cell: (info) => (
        <Link
          to={eventsHref(info.row.original.visitId)}
          className="font-semibold text-link no-underline hover:underline"
        >
          Open visit
        </Link>
      ),
    }),
  ];
  return (
    <Card className="rounded-2xl">
      {/* A plain block, not a named section: the scroll region of the table already carries the name "Visits in this range", and two landmarks may not share one. */}
      <div>
        <CardHeader className="pb-3">
          <CardTitle as="h2" className="text-xl leading-6">
            Visits in this range
          </CardTitle>
          <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
            Newest first. Each visit counts in the range where it started.
          </p>
        </CardHeader>
        <CardContent>
          <DataTable
            caption="Visits in this range"
            columns={columns}
            data={visits}
            getRowId={(row) => row.visitId}
            banded
            emptyState={
              <p className="m-0 p-4 text-sm text-muted-foreground">
                No visit started in this range.
              </p>
            }
          />
          {hasMore ? (
            <Button
              type="button"
              variant="outline"
              loading={loadingMore}
              onClick={onMore}
              className="justify-self-start"
            >
              {loadingMore ? "Loading more visits" : "Show more visits"}
            </Button>
          ) : null}
          <p className="m-0 text-xs text-muted-foreground">
            "Open visit" opens the Events tab with this visit, step by step. "What happened" counts
            the records made while the visit lasted; it does not say that the visit caused them.
          </p>
        </CardContent>
      </div>
    </Card>
  );
}
